import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { highlightText } from 'lib/highlight-text'
import Tooltip from 'components/misc/Tooltip'
import Snippet from './Snippet'
import TypeIcon from 'components/misc/TypeIcon'
import { toggleModal, MODAL_FROM_CONTEXTS } from 'store/ui/actions'
import { updateFlag } from 'store/report/actions'
import toTitleCase from 'lib/title-caser'
import * as analytics from 'lib/analytics'
import CopyButton from 'components/misc/CopyButton'
import DropdownMenu from 'components/misc/DropdownMenu'
import classNames from 'classnames'

import styles from './styles/Citation.css'

const DISPLAY_TYPES = {
  contradicting: 'contrasting'
}

const percent = rating => `${Math.min(Math.round(rating * 100), 99)}%`
export const ClassificationDetails = ({ type, typeConfidence, expertClassified }) => {
  if (type === 'unclassified') {
    return (
      <Tooltip className={styles.tooltip} content='Citation classifications only work on publications we have detected as English'>
        <i className={styles.infoIcon} />
      </Tooltip>
    )
  }

  if (expertClassified) {
    return (
      <div className={styles.classificationDetails}>
        <span className={styles.classificationDetail}>(Expert classified)</span>
      </div>
    )
  }

  return (
    <Tooltip className={styles.tooltip} content='The confidence of our system that this classification is correct. If you feel it is wrong, you can flag it for review below.'>
      <div className={styles.classificationDetails}>
        <span className={styles.classificationDetail}>
          confidence: {percent(typeConfidence)}
        </span>
      </div>
    </Tooltip>
  )
}

export const Citation = ({
  citation, textMatch, textMatches, tally, blur, paper,
  linkTarget = false, snippetRef, handleCopy,
  showClassification = true, showClickToCopy = true,
  truncateSnippet = false
}) => {
  const dispatch = useDispatch()
  const user = useSelector(({ ui }) => ui.user)
  const [showFullContext, setShowFullContext] = useState(false)

  const menuItems = []
  //
  // This is probably a doi_reference
  //
  if (!citation?.id) {
    return null
  }

  useEffect(() => {
    if (!paper) {
      return
    }

    analytics.event({
      category: 'Smart Citation',
      action: 'View Snippet',
      member_id: paper.memberId
    }, { pushToPosthog: false })
  }, [])

  if (citation.type !== 'unclassified' && showClassification) {
    menuItems.push({
      iconEl: <i className='fa fa-flag' alt='Flag report' />,
      label: 'Flag classification',
      handleClick: () => {
        dispatch(updateFlag({ citation: citation.id, comment: '', type: null, citationType: citation.type }))
        dispatch(toggleModal(user?.email ? 'flag' : 'login', true, MODAL_FROM_CONTEXTS.FLAG))
      }
    })
  }

  let matchCandidates = []
  if (textMatches && textMatches.length > 0) {
    matchCandidates = [...textMatches]
  }
  if (textMatch) {
    matchCandidates.push(textMatch)
  }
  // highlight all matches
  const highlightedSnippet = matchCandidates.reduce((acc, match) => {
    if (match === citation.snippet) {
      return acc
    }
    return highlightText(acc, match)
  }, citation.snippet)

  const isHighlighted = highlightedSnippet !== citation.snippet
  // sort by text length
  const largestMatchCandidate = matchCandidates.filter(
    match => match !== citation.snippet && highlightText(citation.snippet, match) !== citation.snippet
  ).sort(
    (a, b) => b.length - a.length
  )[0]
  const snippet = truncateSnippet
    ? (
        showFullContext ? highlightedSnippet : largestMatchCandidate && isHighlighted ? largestMatchCandidate : citation.snippet.slice(0, 125)
      )
    : highlightedSnippet

  return (
    <div className={classNames(styles.citation,
      { [styles.citationGrid]: showClickToCopy }
    )}
    >
      <div className={styles.rightCol}>
        <div className={styles.snippetLayout} ref={snippetRef}>
          <Snippet reportTarget={citation.target} snippet={snippet} linkTarget={linkTarget} blur={citation.snippetHidden} truncateSnippet={truncateSnippet} setShowFullContext={setShowFullContext} showFullContext={showFullContext} />
        </div>
        <div className={styles.citeInfo}>
          {citation?.section && <span className={styles.section}><b>Section</b>: {toTitleCase(citation.section)}</span>}
          {showClassification && (
            <span className={styles.typeInfo}>
              <TypeIcon className={styles.icon} type={citation.type} />
              <span className={styles.citationType}>{DISPLAY_TYPES[citation.type] || citation.type}</span>
              <ClassificationDetails
                type={citation.type}
                typeConfidence={citation.typeConfidence}
                expertClassified={!!citation.expertClassification}
              />
            </span>
          )}
        </div>
      </div>
      {showClickToCopy && (
        <div className={styles.actions}>
          <CopyButton onClick={handleCopy} />
          {menuItems.length > 0 && <DropdownMenu items={menuItems} />}
        </div>
      )}
    </div>
  )
}

export default Citation
